// ------------------------------------------------------------
// useCarSheetsLoader.js — FIXED VERSION
// ------------------------------------------------------------
import { useEffect, useState, useCallback } from "react";
import { collection, getDocs, doc, setDoc, deleteDoc, writeBatch, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase/firebase";
import { buildSheets } from "./buildSheets";
import {
  finalizeSheetTotals,
  createDefaultExpensesTemplate,
  makeMonthLabel,
} from "./helpers";
// In useCarSheetsLoader.js - update imports:
import { loadOrphanedIncome } from "./orphanedIncomeUtils";
import { loadAssignmentsMap } from "../../earningsImport/assignmentUtils";
import {
  applyDriverCostAutomationToSheets,
  loadInstallmentAdjustments,
} from "./buildSheets/autoDriverCosts";
import {
  applyRunningExpensesToSheets,
  loadRunningExpensesForMonth,
} from "./buildSheets/runningExpensesUtils";


export default function useCarSheetsLoader(month, shouldBuild, setShouldBuild, shouldLoad, setShouldLoad) {
  const [carSheets, setCarSheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ------------------------------------------------------------
  // Helper: convert Firestore timestamp → JS Date
  // ------------------------------------------------------------
  const toDateValue = useCallback((v) => {
    if (!v) return v;
    if (v instanceof Date) return v;
    return v.toDate ? v.toDate() : v;
  }, []);

  // ------------------------------------------------------------
  // Normalize Firestore sheet object
  // ------------------------------------------------------------
  const normalizeFirestoreSheet = useCallback(
    (raw) => {
      const sheet = {
        ...raw,
        id: raw.id,
      };
      
      // Safely convert periodStart
      try {
        sheet.periodStart = toDateValue(raw.periodStart);
        if (!(sheet.periodStart instanceof Date) || isNaN(sheet.periodStart.getTime())) {
          sheet.periodStart = null;
        }
      } catch (e) {
        sheet.periodStart = null;
      }
      
      // Safely convert periodEnd
      try {
        sheet.periodEnd = toDateValue(raw.periodEnd);
        if (!(sheet.periodEnd instanceof Date) || isNaN(sheet.periodEnd.getTime())) {
          sheet.periodEnd = null;
        }
      } catch (e) {
        sheet.periodEnd = null;
      }
      
      // Convert driverPeriods dates (supports old object + new array)
sheet.driverPeriods = raw.driverPeriods
  ? Object.fromEntries(
      Object.entries(raw.driverPeriods).map(([driverId, v]) => {
        // NEW: array format
        if (Array.isArray(v)) {
          return [driverId, v.map((p) => ({
            ...p,
            startDate: toDateValue(p.startDate),
            endDate: toDateValue(p.endDate),
          }))];
        }

        // OLD: single object {start,end}
        return [driverId, [{
          driverId,
          driverName: v?.driverName || "",
          startDate: toDateValue(v?.start),
          endDate: toDateValue(v?.end),
        }]];
      })
    )
  : {};

      
      return sheet;
    },
    [toDateValue]
  );
const norm = (s) => String(s || "").trim().toUpperCase();

const getPrevMonth = (mStr) => {
  const [yy, mm] = String(mStr).split("-").map(Number);
  const d = new Date(yy, (mm || 1) - 1, 1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const getMonthStart = (mStr) => {
  const [yy, mm] = String(mStr).split("-").map(Number);
  return new Date(yy, (mm || 1) - 1, 1);
};

const sameDay = (a, b) => {
  if (!a || !b) return false;
  const da = a?.toDate ? a.toDate() : new Date(a);
  const db = b?.toDate ? b.toDate() : new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
};

const getMonthEnd = (mStr) => {
  const [yy, mm] = String(mStr).split("-").map(Number);
  return new Date(yy, mm || 1, 0);
};

const didSheetEndBeforeMonthEnd = (sheet, mStr) => {
  const endValue = sheet?.periodEnd || sheet?.endDate || sheet?.end;
  if (!endValue) return false;

  const endDate = endValue?.toDate ? endValue.toDate() : new Date(endValue);
  if (!endDate || Number.isNaN(endDate.getTime())) return false;

  const monthEnd = getMonthEnd(mStr);
  return endDate.getTime() < monthEnd.getTime();
};

const formatDateId = (date) => {
  const d = date?.toDate ? date.toDate() : new Date(date);
  if (!d || isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

const isManualSheet = (s) =>
  Boolean(s?.isManualDriverSheet) ||
  String(s?.carNumber || "").toUpperCase().startsWith("MANUAL-");

const getPrimaryDriverKey = (sheet) => {
  // Carry-forward balance should follow the driver sheet, not only the car.
  // If a car is reassigned in the new month, the previous driver balance must
  // become its own BF sheet instead of moving into the new driver's sheet.
  try {
    const periods = sheet?.driverPeriods || {};
    const entries = Object.entries(periods)
      .map(([driverId, list]) => {
        const first = Array.isArray(list) ? list[0] : list;
        return {
          driverId,
          driverName: first?.driverName || "",
          startDate: first?.startDate || first?.start || null,
        };
      })
      .filter((x) => x.driverId || x.driverName)
      .sort((a, b) => {
        const da = a.startDate ? new Date(a.startDate).getTime() : 0;
        const db = b.startDate ? new Date(b.startDate).getTime() : 0;
        return da - db;
      });

    if (entries.length) {
      const first = entries[0];
      return norm(first.driverId || first.driverName);
    }
  } catch {}

  if (Array.isArray(sheet?.drivers) && sheet.drivers.length) {
    return norm(sheet.drivers[0]);
  }

  return norm(sheet?.driverName);
};

const getSheetBalancePayable = (sheet) => {
  const row =
    (sheet?.incomeRows || []).find((x) => x?.id === "balancePayable") ||
    (sheet?.incomeRows || []).find((x) =>
      norm(x?.label || x?.company).includes("BALANCE PAYABLE")
    );

  if (row) return Number(row.total || 0);

  const totalIncome = Number(sheet?.totalIncome || 0);
  const totalExpenses = Number(sheet?.totalExpenses || 0);
  return totalIncome - totalExpenses;
};

const getCarryForwardKey = (sheet) => {
  const car = norm(sheet?.carNumber);
  if (!car) return "";

  if (isManualSheet(sheet)) {
    const driver =
      norm(sheet?.driverName) ||
      norm(Array.isArray(sheet?.drivers) ? sheet.drivers.join(",") : "");
    return `MANUAL|${car}|${driver}`;
  }

  const primaryDriver = getPrimaryDriverKey(sheet);
  // Important: AUTO balance is now car + primary driver, not only car.
  // This prevents old driver's June balance/expenses from attaching to a new
  // driver's July sheet when the same car is reassigned after being idle.
  return `AUTO|${car}|${primaryDriver || "NO_DRIVER"}`;
};


const getSheetCarKey = (sheet) => norm(sheet?.carNumber);

const getDriverKeysFromSheet = (sheet) => {
  const keys = new Set();

  const addKey = (value) => {
    const k = norm(value);
    if (k) keys.add(k);
  };

  try {
    Object.entries(sheet?.driverPeriods || {}).forEach(([driverId, periods]) => {
      addKey(driverId);
      const list = Array.isArray(periods) ? periods : [periods];
      list.forEach((p) => {
        addKey(p?.driverId);
        addKey(p?.driverName);
        addKey(p?.name);
      });
    });
  } catch {}

  if (Array.isArray(sheet?.driverIds)) {
    sheet.driverIds.forEach(addKey);
  }

  if (Array.isArray(sheet?.driverNames)) {
    sheet.driverNames.forEach(addKey);
  }

  if (Array.isArray(sheet?.drivers)) {
    sheet.drivers.forEach(addKey);
  }

  addKey(sheet?.driverId);
  addKey(sheet?.driverName);

  return keys;
};

const countDriverOverlap = (prevSheet, curSheet) => {
  const prevKeys = getDriverKeysFromSheet(prevSheet);
  const curKeys = getDriverKeysFromSheet(curSheet);

  let overlap = 0;
  prevKeys.forEach((k) => {
    if (curKeys.has(k)) overlap += 1;
  });

  return overlap;
};

const findCurrentSheetForCarryForward = (group, curSheets, curByKey, monthStart) => {
  // 1) Exact legacy/current key match first.
  const exactMatches = curByKey[group.key] || [];
  let matches = exactMatches.map((sheet) => ({
    sheet,
    overlap: countDriverOverlap(group.representative, sheet),
    exact: true,
  }));

  // 2) Fallback: same car + any shared driver.
  // This is important when one car has two drivers. The primary-driver key can
  // differ between months even though the current sheet already contains one or
  // both previous drivers. In that case we must update the existing July sheet,
  // not create a duplicate balance-forward sheet.
  if (!matches.length) {
    const prevCarKey = group.carKey || getSheetCarKey(group.representative);
    matches = curSheets
      .filter((sheet) => {
        if (!prevCarKey || getSheetCarKey(sheet) !== prevCarKey) return false;
        return countDriverOverlap(group.representative, sheet) > 0;
      })
      .map((sheet) => ({
        sheet,
        overlap: countDriverOverlap(group.representative, sheet),
        exact: false,
      }));
  }

  if (!matches.length) return null;

  matches.sort((a, b) => {
    // Prefer the sheet with more shared drivers.
    if (b.overlap !== a.overlap) return b.overlap - a.overlap;

    // Prefer sheet that starts on month start if available.
    const aSameStart = sameDay(a.sheet.periodStart, monthStart) ? 1 : 0;
    const bSameStart = sameDay(b.sheet.periodStart, monthStart) ? 1 : 0;
    if (bSameStart !== aSameStart) return bSameStart - aSameStart;

    // Otherwise choose the earliest current period for stable behaviour.
    const at = a.sheet?.periodStart ? new Date(a.sheet.periodStart).getTime() : 0;
    const bt = b.sheet?.periodStart ? new Date(b.sheet.periodStart).getTime() : 0;
    return at - bt;
  });

  return matches[0].sheet;
};

const setBalanceBroughtForwardRow = (sheet, amount, prevMonth) => {
  if (!Array.isArray(sheet.expensesRows) || !sheet.expensesRows.length) {
    sheet.expensesRows = createDefaultExpensesTemplate();
  }

  let bfRow = sheet.expensesRows.find(
    (x) =>
      norm(x.label) === "BALANCE BROUGHT FORWARD" ||
      norm(x.label) === "BALANCE CARRY FORWARD"
  );

  if (!bfRow) {
    bfRow = {
      id: `exp_balance_brought_forward_${Date.now()}`,
      label: "BALANCE BROUGHT FORWARD",
      col1: "",
      col2: "",
      col3: "",
      total: 0,
    };
    sheet.expensesRows.push(bfRow);
  }

  bfRow.total = Number(amount || 0);
  bfRow.description = `Balance brought forward from ${prevMonth}`;
};

const forceRecalcIncomeSummaryRows = (sheet) => {
  const incomeDataRows = (sheet.incomeRows || []).filter(
    (r) => r.rowType === "data"
  );

  const expenseDataRows = (sheet.expensesRows || []).filter(
    (r) => !r.isSummary
  );

  const totalIncome = incomeDataRows.reduce(
    (sum, r) => sum + (Number(r.total) || 0),
    0
  );

  const totalExpenses = expenseDataRows.reduce(
    (sum, r) => sum + (Number(r.total) || 0),
    0
  );

  const balance = totalIncome - totalExpenses;
  const balancePayable = balance;

  sheet.incomeRows = incomeDataRows;

  sheet.incomeRows.push(
    {
      id: "totalIncome",
      rowType: "summary",
      label: "TOTAL INCOME OF THE MONTH",
      total: totalIncome,
    },
    {
      id: "balance",
      rowType: "summary",
      label: "BALANCE (Income – Expenses)",
      total: balance,
    },
    {
      id: "balancePayable",
      rowType: "summary",
      label: "BALANCE PAYABLE / RECEIVABLE",
      total: balancePayable,
    }
  );

  const expenseSummaryRow = (sheet.expensesRows || []).find((r) => r.isSummary);
  if (expenseSummaryRow) {
    expenseSummaryRow.total = totalExpenses;
  }

  sheet.totalIncome = totalIncome;
  sheet.totalExpenses = totalExpenses;
  sheet.balance = balance;
  sheet.balancePayable = balancePayable;
  sheet.netProfit = balance;
};

const makeCarryForwardSheetFromPrevious = ({
  prev,
  month,
  prevMonth,
  balanceAmount,
}) => {
  const monthStart = getMonthStart(month);
  const monthEnd = getMonthEnd(month);

  const oldStart = prev.periodStart ? new Date(prev.periodStart) : null;
  const oldEnd = prev.periodEnd ? new Date(prev.periodEnd) : null;

  const oldPeriod =
    oldStart && oldEnd
      ? `${formatDateId(oldStart)} → ${formatDateId(oldEnd)}`
      : prev.periodLabel || prevMonth;

  const safeCar = String(prev.carNumber || "UNKNOWN-CAR").trim();
  const prevIdBase = String(prev.id || safeCar).replace(/[/#?[\]]+/g, "-");

  const sheet = {
    ...prev,

    id: `${prevIdBase}__BF__${month}`,
    month,
    monthLabel: makeMonthLabel(month),

    // Keep old period visible because balance belongs to old driver/old period
    periodStart: oldStart || monthStart,
    periodEnd: oldEnd || monthEnd,
    periodLabel: `BF FROM ${prevMonth} (${oldPeriod})`,

    incomeRows: [],
    cashRows: [],
    expensesRows: createDefaultExpensesTemplate(),

    totalIncome: 0,
    totalCash: 0,
    totalExpenses: 0,
    netProfit: 0,

    isCarryForwardOnly: true,
    carryForwardApplied: true,
    carryForwardFrom: prevMonth,
    carryForwardOnlyOutstandingBalance: true,
    carryForwardSourceSheetId: prev.id || "",
    carryForwardDriverKey: getPrimaryDriverKey(prev),
  };

  if (isManualSheet(prev)) {
    sheet.isManualDriverSheet = true;
  }

  setBalanceBroughtForwardRow(sheet, -Number(balanceAmount || 0), prevMonth);
finalizeSheetTotals(sheet);
forceRecalcIncomeSummaryRows(sheet);

return sheet;
};
  // ------------------------------------------------------------
  // SIMPLE LOAD FUNCTION (Load without deleting income)
  // ------------------------------------------------------------
  const loadExistingSheets = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      
      console.log("📥 MODE: Simple Load (no deletion) - loading existing sheets");
      
      const snap = await getDocs(collection(db, "carSheets", month, "sheets"));
      const firestoreSheets = snap.docs.map((d) =>
        normalizeFirestoreSheet({ id: d.id, ...d.data() })
      );
      
      console.log("📘 Loaded existing sheets:", firestoreSheets.length);
      
      // Sort sheets
      firestoreSheets.sort((a, b) => {
        const carA = String(a.carNumber || "");
        const carB = String(b.carNumber || "");
        if (carA !== carB) return carA.localeCompare(carB);
        
        let aTime = 0;
        let bTime = 0;
        
        try {
          aTime = a.periodStart instanceof Date ? a.periodStart.getTime() : 
                  (typeof a.periodStart === 'string' ? new Date(a.periodStart).getTime() : 0);
        } catch (e) { aTime = 0; }
        
        try {
          bTime = b.periodStart instanceof Date ? b.periodStart.getTime() : 
                  (typeof b.periodStart === 'string' ? new Date(b.periodStart).getTime() : 0);
        } catch (e) { bTime = 0; }
        
        return aTime - bTime;
      });
      
      setCarSheets(firestoreSheets);
      setLoading(false);
      
      // Log sample sheet
      if (firestoreSheets.length > 0) {
        const sampleSheet = firestoreSheets.find(s => s.carNumber === "12456 Private") || firestoreSheets[0];
        console.log("🧪 Sample sheet after load:", {
          id: sampleSheet.id,
          carNumber: sampleSheet.carNumber,
          incomeRows: sampleSheet.incomeRows?.length || 0,
          expensesRows: sampleSheet.expensesRows?.length || 0,
          totalIncome: sampleSheet.totalIncome,
          totalExpenses: sampleSheet.totalExpenses,
          netProfit: sampleSheet.netProfit
        });
      }
      
      return firestoreSheets;
    } catch (err) {
      console.error("❌ Load error:", err);
      setError(err.message || "Failed to load car sheets");
      setLoading(false);
      throw err;
    }
  }, [month, normalizeFirestoreSheet]);
// ------------------------------------------------------------
// ONE-TIME: Carry forward previous month balance + fixed rows
// ------------------------------------------------------------
// ------------------------------------------------------------
// ONE-TIME: Carry forward previous month balance + fixed rows
// ------------------------------------------------------------
const applyCarryForwardOnce = async () => {
  try {
    setLoading(true);
    setError("");

    const monthStart = getMonthStart(month);
    const prevMonth = getPrevMonth(month);

    console.log("🔁 ONE-TIME Carry Forward:", { month, prevMonth });

    const curSnap = await getDocs(collection(db, "carSheets", month, "sheets"));
    const prevSnap = await getDocs(collection(db, "carSheets", prevMonth, "sheets"));

    // Load current assignment-cost rules once. Fixed rows may be copied from the
    // previous month for backward compatibility, but current configured driver
    // costs must win afterwards so a stale ADDITIONAL DRIVER amount cannot be
    // carried into a month where that second driver was removed/promoted.
    const [carryCostAssignmentsMap, carryInstallmentAdjustments] = await Promise.all([
      loadAssignmentsMap(),
      loadInstallmentAdjustments(month),
    ]);

    const curSheets = curSnap.docs.map((d) =>
      normalizeFirestoreSheet({ id: d.id, ...d.data() })
    );

    const prevSheets = prevSnap.docs.map((d) =>
      normalizeFirestoreSheet({ id: d.id, ...d.data() })
    );

    // Recalculate rows before detecting balance
    curSheets.forEach((s) => {
      try {
        finalizeSheetTotals(s);
      } catch (e) {
        console.warn("Could not finalize current sheet:", s.id, e);
      }
    });

    prevSheets.forEach((s) => {
      try {
        finalizeSheetTotals(s);
      } catch (e) {
        console.warn("Could not finalize previous sheet:", s.id, e);
      }
    });

    const fixedLabels = new Set([
      "MONTHLY FIXED COST",
      "TAX",
      "WPS CHARGES",
      "MULKIA/INSURANCE RENEWAL",
      "ADDITIONAL DRIVER",
      "ADDITIONAL DRIVERS",
      "ADDITIONAL DRIVER COST",
    ]);

    // Previous month sheets with non-zero balance
    const prevBalanceGroups = {};

    for (const prev of prevSheets) {
      const key = getCarryForwardKey(prev);
      if (!key) continue;

      const prevBal = getSheetBalancePayable(prev);

      console.log("🧪 Prev sheet balance check:", {
        id: prev.id,
        key,
        carNumber: prev.carNumber,
        drivers: prev.drivers,
        balance: prevBal,
      });

      if (Number(prevBal || 0) === 0) continue;

      if (!prevBalanceGroups[key]) {
        prevBalanceGroups[key] = {
          key,
          carKey: getSheetCarKey(prev),
          balance: 0,
          sheets: [],
          representative: prev,
          endedBeforePrevMonthEnd: didSheetEndBeforeMonthEnd(prev, prevMonth),
        };
      }

      prevBalanceGroups[key].balance += Number(prevBal || 0);
      prevBalanceGroups[key].sheets.push(prev);

      const currentRep = prevBalanceGroups[key].representative;
      const prevEnd = prev.periodEnd ? new Date(prev.periodEnd).getTime() : 0;
      const repEnd = currentRep?.periodEnd ? new Date(currentRep.periodEnd).getTime() : 0;

      if (prevEnd > repEnd) {
        prevBalanceGroups[key].representative = prev;
      }

      prevBalanceGroups[key].endedBeforePrevMonthEnd = didSheetEndBeforeMonthEnd(
        prevBalanceGroups[key].representative,
        prevMonth
      );
    }

    const curByKey = {};

    for (const sheet of curSheets) {
      const key = getCarryForwardKey(sheet);
      if (!key) continue;

      if (!curByKey[key]) curByKey[key] = [];
      curByKey[key].push(sheet);
    }

    const batch = writeBatch(db);
    let updatedCount = 0;
    let createdCount = 0;

    for (const group of Object.values(prevBalanceGroups)) {
      const target = findCurrentSheetForCarryForward(group, curSheets, curByKey, monthStart);

      if (target && target.carryForwardApplied) {
        console.log("⏭️ Carry forward already applied, skipping:", target.id);
        continue;
      }

      const copyFixedRows = !group.endedBeforePrevMonthEnd;

      if (target) {
        const prev = group.representative;

        if (copyFixedRows) {
          const prevExpMap = {};
          (prev.expensesRows || []).forEach((r) => {
            prevExpMap[norm(r.label)] = r;
          });

          (target.expensesRows || []).forEach((r) => {
            const key = norm(r.label);
            const isFixed =
              fixedLabels.has(key) || key.includes("ADDITIONAL DRIVER");

            if (!isFixed) return;

            const curTotal = Number(r.total || 0);
            const curDesc = String(r.description || "").trim();
            const pr = prevExpMap[key];
            if (!pr) return;

            if (curTotal === 0 && !curDesc) {
              r.total = Number(pr.total || 0);
              r.description = pr.description || "";
            }
          });
        } else {
          console.log(
            "ℹ️ Previous driver sheet ended before month end. Carrying only outstanding balance, not fixed expenses:",
            {
              previousSheetId: prev.id,
              currentSheetId: target.id,
              carNumber: prev.carNumber,
              drivers: prev.drivers,
              previousPeriodEnd: prev.periodEnd,
            }
          );
        }

        setBalanceBroughtForwardRow(target, -Number(group.balance || 0), prevMonth);

        // Re-apply the selected month's configured driver cost rules after any
        // fixed-row copy. This preserves legacy copy behavior where nothing is
        // configured, while ensuring configured Main/Second roles are correct.
        try {
          applyDriverCostAutomationToSheets({
            sheets: [target],
            month,
            assignmentsMap: carryCostAssignmentsMap,
            adjustments: carryInstallmentAdjustments,
          });
        } catch (costErr) {
          console.warn(
            "Could not re-apply driver costs after carry forward:",
            target.id,
            costErr
          );
        }

        finalizeSheetTotals(target);
        forceRecalcIncomeSummaryRows(target);

        const ref = doc(db, "carSheets", month, "sheets", target.id);

        batch.set(
          ref,
          {
            expensesRows: target.expensesRows || [],
            incomeRows: target.incomeRows || [],
            cashRows: target.cashRows || [],
            totalIncome: target.totalIncome || 0,
            totalCash: target.totalCash || 0,
            totalExpenses: target.totalExpenses || 0,
            netProfit: target.netProfit || 0,
            balance: target.balance || 0,
            balancePayable: target.balancePayable || 0,

            carryForwardApplied: true,
            carryForwardFrom: prevMonth,
            carryForwardOnlyOutstandingBalance: !copyFixedRows,
            carryForwardAppliedAt: serverTimestamp(),
          },
          { merge: true }
        );

        updatedCount++;
      } else {
        const newSheet = makeCarryForwardSheetFromPrevious({
          prev: group.representative,
          month,
          prevMonth,
          balanceAmount: group.balance,
        });

        const ref = doc(db, "carSheets", month, "sheets", newSheet.id);

        batch.set(
          ref,
          {
            ...newSheet,
            periodStart: newSheet.periodStart ? new Date(newSheet.periodStart) : null,
            periodEnd: newSheet.periodEnd ? new Date(newSheet.periodEnd) : null,
            carryForwardAppliedAt: serverTimestamp(),
          },
          { merge: true }
        );

        console.log("🆕 Created missing carry-forward sheet:", {
          id: newSheet.id,
          carNumber: newSheet.carNumber,
          drivers: newSheet.drivers,
          balanceFromPrevMonth: group.balance,
        });

        createdCount++;
      }
    }

    await batch.commit();

    console.log(
      `✅ Carry-forward applied. Updated: ${updatedCount}, Created missing BF sheets: ${createdCount}`
    );

    await loadExistingSheets();

    return updatedCount + createdCount;
  } catch (err) {
    console.error("❌ applyCarryForwardOnce error:", err);
    setError(err.message || "Failed to carry forward balances");
    throw err;
  } finally {
    setLoading(false);
  }
};

    // ------------------------------------------------------------
  // SAVE SHEETS TO FIRESTORE
  // ------------------------------------------------------------
  const saveSheetsToFirestore = useCallback(async (month, sheets) => {
    try {
      console.log("💾 Saving sheets to Firestore...");
      
      // Clear existing sheets for this month
      const monthRef = collection(db, "carSheets", month, "sheets");
      const existingSnap = await getDocs(monthRef);
      
      const deletePromises = existingSnap.docs.map(d => deleteDoc(d.ref));
      await Promise.all(deletePromises);
      
      // Save new sheets
      const savePromises = sheets.map(sheet => {
        if (!sheet.id) return Promise.resolve();
        
        const sheetRef = doc(db, "carSheets", month, "sheets", sheet.id);
        
        // Prepare data for Firestore (convert Dates to Timestamps)
        const firestoreSheet = {
          ...sheet,
          periodStart: sheet.periodStart ? new Date(sheet.periodStart) : null,
          periodEnd: sheet.periodEnd ? new Date(sheet.periodEnd) : null,
          driverPeriods: sheet.driverPeriods
  ? Object.fromEntries(
      Object.entries(sheet.driverPeriods).map(([driverId, periods]) => {
        const list = Array.isArray(periods) ? periods : [periods];
        return [driverId, list.map((p) => ({
          ...p,
          startDate: p.startDate ? new Date(p.startDate) : (p.start ? new Date(p.start) : null),
          endDate: p.endDate ? new Date(p.endDate) : (p.end ? new Date(p.end) : null),
        }))];
      })
    )
  : {},

          expensesRows: sheet.expensesRows || [],
          incomeRows: sheet.incomeRows || [],
          cashRows: sheet.cashRows || [],
          drivers: sheet.drivers || [],
          company: sheet.company || "",
          carModel: sheet.carModel || "",
          carNumber: sheet.carNumber || "",
          planType: sheet.planType || "UNKNOWN",
          monthLabel: sheet.monthLabel || "",
          periodLabel: sheet.periodLabel || "",
          totalIncome: sheet.totalIncome || 0,
          totalCash: sheet.totalCash || 0,
          totalExpenses: sheet.totalExpenses || 0,
          netProfit: sheet.netProfit || 0,
        };
        
        return setDoc(sheetRef, firestoreSheet, { merge: true });

      });
      
      await Promise.all(savePromises);
      console.log("✅ Saved", sheets.length, "sheets to Firestore");
      
    } catch (err) {
      console.error("❌ Failed to save sheets to Firestore:", err);
      throw err;
    }
  }, []);
  // ------------------------------------------------------------
  // REFRESH FUNCTION (Delete income and rebuild)
  // ------------------------------------------------------------
  const refreshIncomeSheets = useCallback(async () => {
    
    
    try {
      setLoading(true);
      setError("");
      
      console.log("🔄 MODE: Refresh Income (delete + rebuild)");        

      // ------------------------------------------------------------
      // A. LOAD EXISTING SHEETS FROM FIRESTORE (WITH EXPENSES)
      // ------------------------------------------------------------
      const snap = await getDocs(collection(db, "carSheets", month, "sheets"));
      
      const firestoreSheets = snap.docs.map((d) =>
        normalizeFirestoreSheet({ id: d.id, ...d.data() })
      );

      console.log("📘 Firestore sheets loaded:", firestoreSheets.length);

      // ------------------------------------------------------------
      // B. DELETE ALL INCOME FROM EXISTING SHEETS
      // ------------------------------------------------------------
      console.log("🧹 DELETING all old income data from sheets...");
      firestoreSheets.forEach((sheet) => {
  // Manual sheets must keep their manual income rows.
  // Otherwise TOTAL INCOME / BALANCE / PAYABLE rows disappear.
  if (isManualSheet(sheet)) {
    try {
      finalizeSheetTotals(sheet);
    } catch (e) {
      console.warn("Could not finalize manual sheet:", sheet.id, e);
    }
    return;
  }

  sheet.incomeRows = [];
  sheet.cashRows = [];
  sheet.totalIncome = 0;
  sheet.totalCash = 0;
  sheet.netProfit = -Number(sheet.totalExpenses || 0);
});
      console.log("✅ All income cleared from existing sheets");

      // ------------------------------------------------------------
      // C. LOAD FRESH INCOME DATA
      // ------------------------------------------------------------
      console.log("📊 Loading fresh income data...");
      
      // Load weekly earnings
      const weeklyOut = [];
      try {
        const driversSnap = await getDocs(collection(db, "driverWeeklyEarnings"));
        for (const drv of driversSnap.docs) {
          const driverId = drv.id;
          const uploadsRef = collection(
            db,
            "driverWeeklyEarnings",
            driverId,
            "uploads"
          );
          const uploadsSnap = await getDocs(uploadsRef);
          uploadsSnap.forEach((u) => {
            const row = u.data();
            if (row.month === month) weeklyOut.push(row);
          });
        }
      } catch (err) {
        console.error("Error loading weekly earnings:", err);
      }
      
      // Load manual trips
      const manualOut = [];
      try {
        const rootSnap = await getDocs(collection(db, "tripImports"));
        for (const batch of rootSnap.docs) {
          const batchId = batch.id;
          if (!batchId.includes(month)) continue;
          const tripsRef = collection(db, "tripImports", batchId, "trips");
          const tripsSnap = await getDocs(tripsRef);
          tripsSnap.forEach((t) => {
            const row = t.data();
            row.driverFinalPrice = Number(row.driverFinalPrice || 0);
            row.batchDate = row.pickupDate;
            manualOut.push(row);
          });
        }
      } catch (err) {
        console.error("Error loading manual trips:", err);
      }
      
      // Load POS transactions
const posOut = [];
try {
  const posSnap = await getDocs(
    collection(db, "posUploads", month, "records")
  );

  posSnap.forEach((d) => {
    const row = d.data();
    row.weekKey = "week" + row.week;  // ⭐ FIX: Convert week → weekKey
    posOut.push(row);
  });

} catch (err) {
  console.error("Error loading POS transactions:", err);
}
console.log("📊 Fresh income data loaded:", {
  weeklyEarnings: weeklyOut.length,
  manualTrips: manualOut.length,
  posTransactions: posOut.length
});

// Driver cost automation data. This is safe-hybrid:
// configured assignments override only monthly fixed/extra driver rows;
// unconfigured assignments keep the old copied/fetched expense logic.
let costAssignmentsMap = {};
let installmentAdjustments = [];
try {
  costAssignmentsMap = await loadAssignmentsMap();
  installmentAdjustments = await loadInstallmentAdjustments(month);
  console.log("✅ Loaded driver cost automation data", {
    assignmentDrivers: Object.keys(costAssignmentsMap || {}).length,
    installmentAdjustments: installmentAdjustments.length,
  });
} catch (costErr) {
  console.error("❌ Failed to load driver cost automation data:", costErr);
  costAssignmentsMap = {};
  installmentAdjustments = [];
}

// Running expenses automation data (Salik/Darb/Petrol/Parking uploads).
// These are date-based and matched to the correct car sheet period by plate digits.
let runningExpenses = [];
try {
  runningExpenses = await loadRunningExpensesForMonth(month);
  console.log("✅ Loaded running expenses", {
    rows: runningExpenses.length,
  });
} catch (runErr) {
  console.error("❌ Failed to load running expenses:", runErr);
  runningExpenses = [];
}

// ⭐⭐ SIMPLE STEP: Just log orphaned income for now
console.log("⚠️  NOTE: Orphaned income detection is enabled");
console.log("   (Income without assignments will be logged)");

// ⭐⭐ ADD THIS: LOAD ORPHANED INCOME FROM FIRESTORE ⭐⭐
console.log("📥 Loading orphaned income from Firestore...");
let orphanedWeekly = [];
let orphanedManual = [];
try {
  const orphanedResult = await loadOrphanedIncome(month, { includePending: false, includeAssigned: true });
  orphanedWeekly = orphanedResult.weekly;
  orphanedManual = orphanedResult.manual;
  console.log(`✅ Loaded ${orphanedWeekly.length} assigned orphaned weekly, ${orphanedManual.length} assigned orphaned manual`);
} catch (orphanedError) {
  console.error("❌ Failed to load orphaned income:", orphanedError);
  // Continue with empty orphaned arrays
}

// ------------------------------------------------------------
// D. BUILD FRESH INCOME SHEETS
// ------------------------------------------------------------
console.log("⚡⚡⚡ SECTION D: About to call buildSheets() ⚡⚡⚡");

// Add debugging to see the data being passed
console.log("Data being passed to buildSheets:");
console.log("- Month:", month);
console.log("- Weekly earnings count:", weeklyOut.length, "+", orphanedWeekly.length, "assigned orphaned =", weeklyOut.length + orphanedWeekly.length);
console.log("- Manual trips count:", manualOut.length, "+", orphanedManual.length, "assigned orphaned =", manualOut.length + orphanedManual.length);
console.log("- POS transactions count:", posOut.length);
console.log("- Total items:", (weeklyOut.length + orphanedWeekly.length) + (manualOut.length + orphanedManual.length) + posOut.length);

// Wrap in try-catch to catch any errors
let autoSheets = [];
try {
  console.log("🧪 Calling buildSheets() now...");
  autoSheets = await buildSheets({
    month,
    weeklyEarnings: [...weeklyOut, ...orphanedWeekly], // ⭐ Combine regular + orphaned
    manualTrips: [...manualOut, ...orphanedManual],    // ⭐ Combine regular + orphaned
    posTransactions: posOut,
  });
  console.log("✅✅✅ buildSheets() SUCCESS! Returned", autoSheets.length, "sheets");
} catch (buildError) {
  console.error("❌❌❌ buildSheets() FAILED with error:", buildError);
  console.error("Error message:", buildError.message);
  console.error("Error stack:", buildError.stack);
  // Continue with empty sheets
  autoSheets = [];
}

console.log("🟩 Auto-generated income sheets:", autoSheets.length);

      // ------------------------------------------------------------
      // E. CREATE CONSISTENT ID
      // ------------------------------------------------------------
      function createConsistentId(sheet, month) {
  if (isManualSheet(sheet)) {
    return sheet.id || "";
  }

  if (!sheet.carNumber || !sheet.periodStart || !sheet.periodEnd) {
    return sheet.id || "";
  }
        
        // Format dates consistently: YYYY-MM-DD
        const formatDate = (date) => {
          if (!date) return "";
          const d = new Date(date);
          if (isNaN(d.getTime())) return "";
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        };
        
        // Use pipe separator consistently
        return `${sheet.carNumber}|${sheet.month || month}|${formatDate(sheet.periodStart)}|${formatDate(sheet.periodEnd)}`;
      }

      // Update IDs to match
      firestoreSheets.forEach(sheet => {
        const newId = createConsistentId(sheet, month);
        if (newId && sheet.id !== newId) {
          sheet.id = newId;
        }
      });

      autoSheets.forEach(sheet => {
        const newId = createConsistentId(sheet, month);
        if (newId && sheet.id !== newId) {
          sheet.id = newId;
        }
      });

           // ------------------------------------------------------------
// G. ADD FRESH INCOME TO SHEETS (PERIOD-AWARE MERGE)
// ------------------------------------------------------------
console.log("➕ Merging fresh income into sheets (period-aware)...");


const manualSheets = firestoreSheets.filter(isManualSheet).map((s) => {
  try {
    finalizeSheetTotals(s);
  } catch (e) {
    console.warn("Could not finalize manual sheet before merge:", s.id, e);
  }
  return s;
});
const existingAutoSheets = firestoreSheets.filter((s) => !isManualSheet(s));

const toStartMs = (d) => {
  try {
    const x = d instanceof Date ? new Date(d) : new Date(d);
    x.setHours(0, 0, 0, 0);
    return x.getTime();
  } catch {
    return 0;
  }
};

const toEndMs = (d) => {
  try {
    const x = d instanceof Date ? new Date(d) : new Date(d);
    x.setHours(23, 59, 59, 999);
    return x.getTime();
  } catch {
    return 0;
  }
};

const overlapMs = (a, b) => {
  const s = Math.max(toStartMs(a.periodStart), toStartMs(b.periodStart));
  const e = Math.min(toEndMs(a.periodEnd), toEndMs(b.periodEnd));
  return Math.max(0, e - s);
};

const bestExistingMatch = (auto) => {
  if (!auto) return null;

  // 1) Exact id match
  const exact = existingAutoSheets.find((s) => s?.id && auto?.id && s.id === auto.id);
  if (exact) return exact;

  // 2) Same carNumber + max overlap period
  const sameCar = existingAutoSheets.filter(
    (s) => String(s.carNumber || "") === String(auto.carNumber || "")
  );
  if (!sameCar.length) return null;

  let best = null;
  let bestScore = 0;
  for (const c of sameCar) {
    const score = overlapMs(c, auto);
    if (score > bestScore) {
      best = c;
      bestScore = score;
    }
  }
  return best;
};

const expenseSum = (sheet) => {
  try {
    return (sheet?.expensesRows || [])
      .filter((r) => !r?.isSummary)
      .reduce((acc, r) => acc + (Number(r?.total) || 0), 0);
  } catch {
    return 0;
  }
};

// AutoSheets are authoritative for which periods should exist.
// We preserve expensesRows from the best matching old sheet.
const usedExistingIds = new Set();

const mergedAutoSheets = (autoSheets || [])
  .filter(Boolean)
  .map((auto) => {
    if (!auto?.id) return null;

    const base = bestExistingMatch(auto);
    if (base?.id) usedExistingIds.add(base.id);

    const merged = {
      ...auto,
      expensesRows: base?.expensesRows ? base.expensesRows : (auto.expensesRows || []),
    };

    // Apply configured main-driver installment / second-driver expense after
    // preserving old expenses. If no cost is configured, this does nothing,
    // so your old carry-forward/copy logic remains active.
    applyDriverCostAutomationToSheets({
      sheets: [merged],
      month,
      assignmentsMap: costAssignmentsMap,
      adjustments: installmentAdjustments,
    });

    finalizeSheetTotals(merged);
    return merged;
  })
  .filter(Boolean);

// Keep old sheets that are NOT represented by autoSheets (e.g., expenses-only cars)
const leftoverExisting = existingAutoSheets.filter((s) => !usedExistingIds.has(s.id));
const keepLeftover = leftoverExisting.filter((s) => {
  const exp = expenseSum(s);
  const hasDrivers = Array.isArray(s?.drivers) && s.drivers.length > 0;
  return exp !== 0 || hasDrivers;
});

keepLeftover.forEach((s) => {
  try {
    applyDriverCostAutomationToSheets({
      sheets: [s],
      month,
      assignmentsMap: costAssignmentsMap,
      adjustments: installmentAdjustments,
    });
    finalizeSheetTotals(s);
  } catch {}
});

const allSheets = [...manualSheets, ...mergedAutoSheets, ...keepLeftover];

try {
  // Apply global driver-cost automation once to the full sheet list.
  // This is required for WPS CHARGES because one driver can work in multiple
  // cars in the same month, and WPS must be charged only to the car sheet where
  // that driver worked the most days.
  applyDriverCostAutomationToSheets({
    sheets: allSheets,
    month,
    assignmentsMap: costAssignmentsMap,
    adjustments: installmentAdjustments,
    includeWps: true,
    wpsFeePerDriver: 25,
  });

  allSheets.forEach((sheet) => {
    try {
      finalizeSheetTotals(sheet);
    } catch (finalizeErr) {
      console.warn("Could not finalize sheet after global driver costs:", sheet?.id, finalizeErr);
    }
  });
} catch (globalCostErr) {
  console.error("❌ Failed to apply global driver costs/WPS:", globalCostErr);
}

try {
  const runningResult = applyRunningExpensesToSheets({
    sheets: allSheets,
    expenses: runningExpenses,
  });
  console.log("✅ Running expenses applied", runningResult);
} catch (runApplyErr) {
  console.error("❌ Failed to apply running expenses:", runApplyErr);
}

console.log("✅ Income refresh complete");
console.log("📊 Sheets after refresh: ", allSheets.length);


      // ------------------------------------------------------------
      // H. REMOVE DUPLICATES AND SORT
      // ------------------------------------------------------------
      const uniqueSheets = [];
      const seenIds = new Set();
      
      allSheets.forEach(sheet => {
        if (!sheet.id) return;
        if (!seenIds.has(sheet.id)) {
          seenIds.add(sheet.id);
          uniqueSheets.push(sheet);
        }
      });

      console.log("✅ Unique sheets after deduplication:", uniqueSheets.length);

      // Sort sheets
      uniqueSheets.sort((a, b) => {
        const carA = String(a.carNumber || "");
        const carB = String(b.carNumber || "");
        if (carA !== carB) return carA.localeCompare(carB);
        
        let aTime = 0;
        let bTime = 0;
        
        try {
          aTime = a.periodStart instanceof Date ? a.periodStart.getTime() : 
                  (typeof a.periodStart === 'string' ? new Date(a.periodStart).getTime() : 0);
        } catch (e) { aTime = 0; }
        
        try {
          bTime = b.periodStart instanceof Date ? b.periodStart.getTime() : 
                  (typeof b.periodStart === 'string' ? new Date(b.periodStart).getTime() : 0);
        } catch (e) { bTime = 0; }
        
        return aTime - bTime;
      });

            
      // ------------------------------------------------------------
      // I. LOG SAMPLE FOR VERIFICATION
      // ------------------------------------------------------------
      if (uniqueSheets.length > 0) {
        const sampleSheet = uniqueSheets.find(s => s.carNumber === "12456 Private") || uniqueSheets[0];
        console.log("🧪 Sample sheet after refresh:", {
          id: sampleSheet.id,
          carNumber: sampleSheet.carNumber,
          incomeRows: sampleSheet.incomeRows?.length || 0,
          expensesRows: sampleSheet.expensesRows?.length || 0,
          totalIncome: sampleSheet.totalIncome,
          totalExpenses: sampleSheet.totalExpenses,
          netProfit: sampleSheet.netProfit
        });
      }

      // ------------------------------------------------------------
      // II. SAVE TO FIRESTORE (CRITICAL FIX!)
      // ------------------------------------------------------------
      console.log("💾 Saving refreshed sheets to Firestore...");
      try {
        await saveSheetsToFirestore(month, uniqueSheets);
        console.log("✅ Successfully saved to Firestore");
      } catch (saveErr) {
        console.error("❌ Failed to save to Firestore:", saveErr);
        // Don't fail the whole operation
        if (!error) setError("Sheets refreshed but save to database failed");
      }

      // ------------------------------------------------------------
      // III. SET FINAL SHEETS
      // ------------------------------------------------------------
      setCarSheets(uniqueSheets);
      setLoading(false);

    } catch (err) {
      console.error("❌ Refresh error:", err);
      setError(err.message || "Failed to refresh income sheets");
      setLoading(false);
      throw err;
    }
  }, [month, normalizeFirestoreSheet, saveSheetsToFirestore, error]);

  // ------------------------------------------------------------
  // MAIN EFFECT - SEPARATE LOGIC FOR LOAD VS REFRESH
  // ------------------------------------------------------------
  useEffect(() => {
    // Reset triggers if they're both false (initial load)
    if (!shouldBuild && !shouldLoad) {
      // You might want to load initially, or just return
      return;
    }

    async function handleAction() {
      try {
        if (shouldLoad) {
          console.log("🚀 Triggering LOAD action");
          await loadExistingSheets();
          if (setShouldLoad) setShouldLoad(false);
        } 
        
        if (shouldBuild) {
          console.log("🚀 Triggering REFRESH action");
          await refreshIncomeSheets();
          if (setShouldBuild) setShouldBuild(false);
        }
      } catch (err) {
        console.error("❌ Action error:", err);
        setError(err.message || "Failed to perform action");
      }
    }

    handleAction();
  }, [
    month, 
    shouldBuild, 
    shouldLoad, 
    loadExistingSheets, 
    refreshIncomeSheets,
    setShouldBuild,
    setShouldLoad
  ]);

  return {
  carSheets,
  loading,
  error,
  loadExistingSheets,
  refreshIncomeSheets,
  applyCarryForwardOnce,
};

}